import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  RefreshCw, Search, Activity, User, CheckCircle2, XCircle, Trash2, Mail,
  FileText, ShieldAlert, Download as DownloadIcon, FileEdit, Wand2,
  Image as ImageIcon, Sparkles, ScrollText, TrendingUp, Filter, ChevronDown,
  ChevronUp, Copy, Upload, Send, PauseCircle, Unlock, Settings2, Link2,
} from "lucide-react";

const NEXON = { indigo: "#636AC3", ink: "#1E1E3F", mint: "#C9E9A4", cloud: "#FAF9FE", ash: "#7A7A99" };

/* Map of action → {icon, tone, label} */
const ACTION_META = {
  status_change:            { icon: CheckCircle2, tone: "emerald", label: "Status change" },
  mark_paid:                { icon: CheckCircle2, tone: "emerald", label: "Marked paid" },
  delete:                   { icon: Trash2,       tone: "rose",    label: "Submission deleted" },
  bulk_approve:             { icon: CheckCircle2, tone: "emerald", label: "Bulk approve" },
  bulk_reject:              { icon: XCircle,      tone: "rose",    label: "Bulk reject" },
  bulk_under_review:        { icon: Activity,     tone: "amber",   label: "Bulk under review" },
  bulk_delete:              { icon: Trash2,       tone: "rose",    label: "Bulk delete" },
  export_submissions_csv:   { icon: DownloadIcon, tone: "sky",     label: "Submissions CSV exported" },
  export_analytics_csv:     { icon: DownloadIcon, tone: "sky",     label: "Analytics CSV exported" },
  kb_create:                { icon: FileEdit,     tone: "indigo",  label: "KB entry created" },
  kb_update:                { icon: FileEdit,     tone: "indigo",  label: "KB entry updated" },
  kb_delete:                { icon: Trash2,       tone: "rose",    label: "KB entry deleted" },
  blog_create:              { icon: FileText,     tone: "indigo",  label: "Blog post created" },
  blog_update:              { icon: FileText,     tone: "indigo",  label: "Blog post updated" },
  blog_delete:              { icon: Trash2,       tone: "rose",    label: "Blog post deleted" },
  blog_auto_generate:       { icon: Wand2,        tone: "mint",    label: "Blog auto-generated" },
  blog_regen_hero:          { icon: ImageIcon,    tone: "mint",    label: "Hero image regenerated" },
  vendor_report_upload:     { icon: Upload,       tone: "emerald", label: "Vendor report uploaded" },
  vendor_report_delete:     { icon: Trash2,       tone: "rose",    label: "Vendor report deleted" },
  support_sync:             { icon: RefreshCw,    tone: "indigo",  label: "Support inbox synced" },
  support_approve_send:     { icon: Send,         tone: "emerald", label: "Reply approved & sent" },
  support_reject:           { icon: XCircle,      tone: "rose",    label: "Reply rejected" },
  support_regenerate:       { icon: Wand2,        tone: "mint",    label: "Draft regenerated" },
  support_config_save:      { icon: Settings2,    tone: "indigo",  label: "Support config saved" },
  support_sender_pause:     { icon: PauseCircle,  tone: "amber",   label: "Sender paused" },
  support_sender_resume:    { icon: Activity,     tone: "emerald", label: "Sender resumed" },
  support_unlock:           { icon: Unlock,       tone: "amber",   label: "Dispatcher unlocked" },
  support_test_connection:  { icon: ShieldAlert,  tone: "sky",     label: "Connection tested" },
  support_test_alert:       { icon: ShieldAlert,  tone: "sky",     label: "Test alert sent" },
  auto_reply_save:          { icon: Sparkles,     tone: "indigo",  label: "Auto-reply saved" },
  auto_reply_delete:        { icon: Trash2,       tone: "rose",    label: "Auto-reply removed" },
  support_two_way_connect:  { icon: Link2,        tone: "emerald", label: "Two-way Telegram connected" },
  support_two_way_disconnect:{ icon: Link2,       tone: "slate",   label: "Two-way Telegram disconnected" },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rose:    "bg-rose-100 text-rose-700 border-rose-200",
  amber:   "bg-amber-100 text-amber-700 border-amber-200",
  sky:     "bg-sky-100 text-sky-700 border-sky-200",
  indigo:  "bg-indigo-100 text-indigo-700 border-indigo-200",
  mint:    "bg-lime-100 text-lime-700 border-lime-200",
  slate:   "bg-slate-100 text-slate-700 border-slate-200",
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date(), diff = (now - d) / 1000;
  if (diff < 60)     return `${Math.floor(diff)}s ago`;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};
const fmtFull = (iso) => iso ? new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/audit-logs", { params: { limit: 500 } });
      setItems(r.data || []);
    } catch { setItems([]); toast.error("Couldn't load audit trail"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = items;
    if (actionFilter !== "all") rows = rows.filter((r) => r.action === actionFilter);
    if (q.trim()) {
      const needle = q.toLowerCase().trim();
      rows = rows.filter((r) =>
        (r.actor || "").toLowerCase().includes(needle) ||
        (r.action || "").toLowerCase().includes(needle) ||
        (r.target_id || "").toLowerCase().includes(needle) ||
        JSON.stringify(r.meta || {}).toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [items, q, actionFilter]);

  const actionsList = useMemo(() => Array.from(new Set(items.map((i) => i.action))).sort(), [items]);

  const kpi = useMemo(() => {
    const total = items.length;
    const today = new Date().toDateString();
    const todayCount = items.filter((i) => new Date(i.timestamp).toDateString() === today).length;
    const actors = new Set(items.map((i) => i.actor || "system")).size;
    const distinct = new Set(items.map((i) => i.action)).size;
    return { total, todayCount, actors, distinct };
  }, [items]);

  const copyRow = async (r) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(r, null, 2));
      toast.success("Row copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 lg:p-8" style={{ backgroundColor: NEXON.cloud }} data-testid="admin-audit-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: NEXON.indigo }}>Audit log</span>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-1" style={{ color: NEXON.ink }}>
              Every admin action, immutable
            </h1>
            <p className="text-sm mt-1" style={{ color: NEXON.ash }}>
              Status changes · deletes · exports · bulk ops · vendor reports · KB &amp; blog edits · support inbox activity. Ordered newest-first.
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.ink}, ${NEXON.mint})` }} />
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-black text-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* KPI row */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "total",  label: "TOTAL ENTRIES",   val: kpi.total,      tone: "bg-indigo-100 text-indigo-800", Icon: ScrollText },
            { k: "today",  label: "TODAY",           val: kpi.todayCount, tone: "bg-emerald-100 text-emerald-800", Icon: Sparkles },
            { k: "actors", label: "DISTINCT ACTORS", val: kpi.actors,     tone: "bg-amber-100 text-amber-800",   Icon: User },
            { k: "kinds",  label: "ACTION KINDS",    val: kpi.distinct,   tone: "bg-slate-100 text-slate-700",   Icon: TrendingUp },
          ].map((s) => (
            <div key={s.k} data-testid={`audit-kpi-${s.k}`} className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest ${s.tone}`}>{s.label}</span>
                <s.Icon className="w-4 h-4 text-slate-300" />
              </div>
              <div className="mt-2 text-3xl font-black tabular-nums" style={{ color: NEXON.ink }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Search + filter chip strip */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search actor, action, target id, metadata…"
              data-testid="audit-search"
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
            <FilterChip
              label={`All · ${items.length}`}
              active={actionFilter === "all"}
              onClick={() => setActionFilter("all")}
              testid="audit-filter-all"
            />
            {actionsList.map((act) => {
              const meta = ACTION_META[act];
              const Icon = meta?.icon || ShieldAlert;
              const count = items.filter((i) => i.action === act).length;
              return (
                <FilterChip
                  key={act}
                  label={meta?.label || act}
                  count={count}
                  active={actionFilter === act}
                  onClick={() => setActionFilter(act)}
                  Icon={Icon}
                  tone={meta?.tone || "slate"}
                  testid={`audit-filter-${act}`}
                />
              );
            })}
          </div>
          {(q || actionFilter !== "all") && (
            <div className="flex items-center justify-between text-xs">
              <span className="inline-flex items-center gap-1.5 text-slate-500 font-black">
                <Filter size={12} /> Showing {filtered.length} of {items.length}
              </span>
              <button
                onClick={() => { setQ(""); setActionFilter("all"); }}
                className="text-slate-500 hover:text-slate-900 font-black"
                data-testid="audit-clear"
              >Clear filters</button>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-14 text-center" data-testid="audit-empty">
              <Activity className="w-10 h-10 mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500 text-sm">
                {loading ? "Loading the trail…" : "No audit entries match your filter."}
              </p>
            </div>
          ) : (
            <ol className="divide-y divide-slate-100 relative">
              <div className="absolute left-[35px] top-4 bottom-4 w-px bg-slate-100 pointer-events-none" />
              {filtered.map((r, i) => {
                const meta = ACTION_META[r.action] || { icon: ShieldAlert, tone: "slate", label: r.action };
                const Icon = meta.icon;
                const tone = TONE_CLASSES[meta.tone] || TONE_CLASSES.slate;
                const hasMeta = r.meta && Object.keys(r.meta).length > 0;
                const rid = r.id || `row-${i}`;
                const isOpen = !!expanded[rid];
                return (
                  <li
                    key={rid}
                    data-testid={`audit-row-${i}`}
                    className="relative px-3 sm:px-5 py-4 hover:bg-slate-50/60 flex items-start gap-3 transition-colors"
                  >
                    <div className={`relative z-10 w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 shadow-sm ${tone}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        <span className="font-black" style={{ color: NEXON.ink }}>{meta.label}</span>
                        {r.action === "status_change" && r.meta?.to && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-black bg-indigo-50 text-indigo-700 border border-indigo-100">
                            → {r.meta.to}
                          </span>
                        )}
                        {r.meta?.count && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-black bg-slate-100 text-slate-700">
                            ×{r.meta.count}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span className="font-bold text-slate-700">{r.actor || "system"}</span>
                        </span>
                        {(r.target_type || r.target_id) && (
                          <span className="inline-flex items-center gap-1">
                            <Mail className="w-3 h-3 opacity-50" />
                            <span className="font-mono text-[11px] truncate max-w-[120px] sm:max-w-[220px]">
                              {r.target_type || "target"}:{(r.target_id || "").slice(0, 14)}
                              {(r.target_id || "").length > 14 ? "…" : ""}
                            </span>
                          </span>
                        )}
                        <span title={fmtFull(r.timestamp)} className="font-black text-slate-400">{fmtRelative(r.timestamp)}</span>
                      </div>
                      {hasMeta && (
                        <div className="mt-2">
                          <button
                            onClick={() => setExpanded((e) => ({ ...e, [rid]: !e[rid] }))}
                            className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-700"
                            data-testid={`audit-meta-toggle-${i}`}
                          >
                            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} metadata
                          </button>
                          {isOpen && (
                            <div className="relative mt-1.5">
                              <pre className="p-3 rounded-lg bg-slate-50 border border-slate-100 font-mono text-[10.5px] text-slate-700 overflow-x-auto whitespace-pre-wrap max-h-64">
                                {JSON.stringify(r.meta, null, 2)}
                              </pre>
                              <button
                                onClick={() => copyRow(r)}
                                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-700"
                                data-testid={`audit-copy-${i}`}
                              ><Copy size={10} /> Copy</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <p className="text-xs text-slate-400 px-1 mt-4">
          Showing latest {items.length} entries. Audit rows are immutable and retained indefinitely.
        </p>
      </div>
    </AdminLayout>
  );
}

function FilterChip({ label, count, active, onClick, Icon, tone = "slate", testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-black transition-all whitespace-nowrap flex-shrink-0 ${
        active
          ? "text-white shadow-sm"
          : `bg-white text-slate-700 border-slate-200 hover:border-indigo-300`
      }`}
      style={active ? { backgroundColor: NEXON.ink, borderColor: NEXON.ink } : {}}
    >
      {Icon && <Icon className={`w-3.5 h-3.5 ${active ? "" : ""}`} />}
      <span>{label}</span>
      {count !== undefined && (
        <span className={`text-[10px] font-black ${active ? "opacity-80" : "text-slate-400"}`}>
          {count}
        </span>
      )}
    </button>
  );
}
